/**
 * Section-based autosave hook
 * 
 * Purpose: Debounced, serialized autosave for individual survey sections
 * Benefits:
 * - Reduces IO pressure (saves only changed section, not entire survey)
 * - Prevents overlapping saves
 * - FastField-like UX (auto-saves progress without user action)
 * - Scalable to 1000+ concurrent users
 */

import { useEffect, useRef, useCallback } from 'react';
import { saveMeasureSpecificationSection } from '../utils/retrofitSurveySectionStorage';

interface UseSectionAutosaveOptions {
  surveyId?: string;
  sectionData: any;
  sectionName: 'measure_specification' | 'room_details' | 'floor_plan'; // Extend as needed
  enabled?: boolean;
  debounceMs?: number; // Time to wait after last change before saving
  minIntervalMs?: number; // Minimum time between saves
}

export function useSectionAutosave({
  surveyId,
  sectionData,
  sectionName,
  enabled = true,
  debounceMs = 3000, // 3 seconds after last change
  minIntervalMs = 10000 // Minimum 10 seconds between saves
}: UseSectionAutosaveOptions) {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveTimeRef = useRef<number>(0);
  const saveInProgressRef = useRef<boolean>(false);
  const lastSavedDataRef = useRef<string>('');
  const pendingSaveRef = useRef<boolean>(false);

  // Save function for the specific section
  const saveSection = useCallback(async () => {
    if (!surveyId || !sectionData) {
      console.log('[SectionAutosave] Skipping save - missing surveyId or data');
      return;
    }

    if (saveInProgressRef.current) {
      console.log('[SectionAutosave] Save already in progress, marking as pending');
      pendingSaveRef.current = true;
      return;
    }

    // Check if data actually changed
    const currentDataString = JSON.stringify(sectionData);
    if (currentDataString === lastSavedDataRef.current) {
      console.log('[SectionAutosave] Data unchanged, skipping save');
      return;
    }

    // Check minimum interval
    const now = Date.now();
    const timeSinceLastSave = now - lastSaveTimeRef.current;
    if (timeSinceLastSave < minIntervalMs && lastSaveTimeRef.current > 0) {
      console.log(`[SectionAutosave] Minimum interval not met (${timeSinceLastSave}ms < ${minIntervalMs}ms), deferring save`);
      // Schedule retry after minimum interval
      setTimeout(() => {
        if (pendingSaveRef.current) {
          saveSection();
        }
      }, minIntervalMs - timeSinceLastSave);
      return;
    }

    saveInProgressRef.current = true;
    const startTime = performance.now();

    try {
      console.log(`[SectionAutosave] 💾 Saving ${sectionName} section:`, {
        surveyId,
        dataSize: currentDataString.length,
        timestamp: new Date().toISOString()
      });

      let result;
      switch (sectionName) {
        case 'measure_specification':
          result = await saveMeasureSpecificationSection(surveyId, sectionData);
          break;
        // Add other sections as they're migrated:
        // case 'room_details':
        //   result = await saveRoomDetailsSection(surveyId, sectionData);
        //   break;
        default:
          throw new Error(`Unknown section: ${sectionName}`);
      }

      if (result.success) {
        const duration = performance.now() - startTime;
        console.log(`[SectionAutosave] ✅ Saved ${sectionName} section:`, {
          surveyId,
          duration: `${duration.toFixed(2)}ms`,
          dataSize: `${(currentDataString.length / 1024).toFixed(2)} KB`,
          timestamp: new Date().toISOString()
        });

        // Update last saved data and time
        lastSavedDataRef.current = currentDataString;
        lastSaveTimeRef.current = Date.now();
        pendingSaveRef.current = false;
      } else {
        console.error(`[SectionAutosave] ❌ Failed to save ${sectionName}:`, result.error);
      }
    } catch (error) {
      console.error(`[SectionAutosave] ❌ Exception saving ${sectionName}:`, error);
    } finally {
      saveInProgressRef.current = false;

      // If there's a pending save, trigger it after a short delay
      if (pendingSaveRef.current) {
        setTimeout(() => {
          if (pendingSaveRef.current) {
            saveSection();
          }
        }, 1000);
      }
    }
  }, [surveyId, sectionData, sectionName, minIntervalMs]);

  // Debounced save effect
  useEffect(() => {
    if (!enabled || !surveyId || !sectionData) {
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      saveSection();
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [sectionData, enabled, surveyId, debounceMs, saveSection]);

  // Save on unmount (if data changed)
  useEffect(() => {
    return () => {
      const currentDataString = JSON.stringify(sectionData);
      if (currentDataString !== lastSavedDataRef.current && surveyId && sectionData) {
        console.log(`[SectionAutosave] 🔄 Component unmounting, forcing save of ${sectionName}`);
        // Force immediate save on unmount
        saveSection();
      }
    };
  }, []); // Empty deps - only run on unmount

  // Return manual save function
  return {
    saveNow: saveSection,
    isSaving: saveInProgressRef.current
  };
}
